import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'
import { IconButton, Text, TextButton, Box, Skeleton } from 'components'

import NotiEmpty from 'assets/illustration-empty-notification.svg'

import { useTranslation } from 'react-i18next'
import { Z_INDEX } from 'utils/constants'
import { createPortal } from 'react-dom'

type Props = {
  allReadLoading: boolean
  padded?: boolean
  title?: React.ReactNode
  isEmpty?: boolean
  isLoading?: boolean
  unReadCount?: number
  onMarkAllReadClick: () => void
  onPanelClose: () => void
  children: React.ReactNode
}

const NotificationPanel = forwardRef<HTMLDivElement, Props>(
  (
    { padded, title, isEmpty, isLoading, allReadLoading, children, unReadCount, onMarkAllReadClick, onPanelClose },
    ref,
  ) => {
    const { t } = useTranslation()

    return (
      <Box onClick={(e) => e.stopPropagation()}>
        {createPortal(
          <Panel ref={ref}>
            <PanelContainer>
              <TitleWrapper>
                <TitleControls>
                  <Title fontWeight="bold" fontSize="16px">
                    {title}
                  </Title>
                  {unReadCount && unReadCount > 0 && (
                    <TextButton
                      marginRight="12"
                      onClick={onMarkAllReadClick}
                      isSubmitting={allReadLoading}
                      disabled={allReadLoading}
                    >
                      {t(`notification_mark_all_read`, { count: unReadCount })}
                    </TextButton>
                  )}
                </TitleControls>
                <IconButton variant="ghost" icon="times" onClick={onPanelClose} />
              </TitleWrapper>
              <Content padded={padded} isEndList={!isEmpty && isLoading}>
                {children}
                {isEmpty && (
                  <EmptyWrapper>
                    <NotificationEmpty />
                    <Text fontWeight="bold" padding="20px">
                      No notifications yet
                    </Text>
                    <Text
                      paddingTop="10px"
                      paddingLeft="20px"
                      paddingRight="20px"
                      textAlign="center"
                      color="gray1"
                      lineHeight="18px"
                    >
                      This is the place where we notify you if there are any changes happening to the records that you
                      are working on.
                    </Text>
                  </EmptyWrapper>
                )}
              </Content>

              {!isEmpty && isLoading && (
                <Footer padded={padded}>
                  <FooterWrapper>
                    <Box paddingTop="10px" paddingLeft="20px" paddingRight="20px" width="90%">
                      <Skeleton height="10px" />
                    </Box>
                    <Box paddingTop="10px" paddingLeft="20px" paddingRight="20px" width="90%">
                      <Skeleton height="10px" />
                    </Box>
                    <Box paddingTop="10px" paddingLeft="20px" paddingRight="20px" width="90%">
                      <Skeleton height="10px" />
                    </Box>
                  </FooterWrapper>
                </Footer>
              )}
            </PanelContainer>
          </Panel>,
          document.body,
        )}
      </Box>
    )
  },
)

const Panel = styled.div`
  position: fixed;
  inset: 0 auto auto 0;
  transform: translate(calc(100vw - 120%), 62px);
  overflow: initial;
  z-index: ${Z_INDEX.notification};
`
const TitleControls = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  padding-right: 16px;
`
const Title = styled(Text)`
  flex-grow: 1;
`

const EmptyWrapper = styled.div`
  position: relative;
  top: 30%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const FooterWrapper = styled.div`
  position: relative;
  top: 10%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const NotificationEmpty = styled(NotiEmpty)`
  display: flex;
`

const PanelContainer = styled.div`
  width: 444px;
  height: calc(100vh - 100px);
  display: flex;
  border-radius: 4px;
  background: white;
  box-shadow: 0 0 12px 1px rgba(0, 0, 0, 0.2);
  flex-direction: column;
`

const TitleWrapper = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.colors.gray4};
  height: 20px;
  padding: 21px 24px 18px;
  text-align: left;
  width: auto;
  display: flex;
  align-items: center;
`

const Content = styled.div<{ padded?: boolean; isEndList?: boolean }>`
  flex-grow: 4;
  max-height: ${({ isEndList }) => (isEndList ? `70vh` : `100vh`)};
  ${(p) =>
    p.padded &&
    css`
      padding: 18px 24px;
    `}
`

const Footer = styled.div<{ padded?: boolean }>`
  flex-grow: 1;
  max-height: 12vh;
  ${(p) =>
    p.padded &&
    css`
      padding: 18px 24px;
    `}
`
export default NotificationPanel
